import { useCallback } from 'react';
import { useMutation } from '@apollo/client';

import { IrlEventTrackingEvent, TrackIrlEventDocument, TrackIrlEventByDispensaryDocument } from 'src/gql/graphql';
import useUser from 'hooks/use-user';
import { Session } from 'utils/session';
import { useIrlEventParams } from './use-irl-event-params';

export type UseIrlEventTrackingReturn = {
  trackEvent: (event: IrlEventTrackingEvent) => Promise<void>;
  called: boolean;
  loading: boolean;
  error: Error | undefined;
  success: boolean;
  completed: boolean;
  failed: boolean;
};

export function useIrlEventTracking(): UseIrlEventTrackingReturn {
  const [
    trackEnterprise,
    {
      called: enterpriseMutationCalled,
      loading: enterpriseMutationLoading,
      error: enterpriseMutationError = undefined,
      data: enterpriseMutationData,
    },
  ] = useMutation(TrackIrlEventDocument);
  const [
    trackDispensary,
    {
      called: dispensaryMutationCalled,
      loading: dispensaryMutationLoading,
      error: dispensaryMutationError = undefined,
      data: dispensaryMutationData,
    },
  ] = useMutation(TrackIrlEventByDispensaryDocument);

  const {
    irlEventId,
    enterpriseId,
    isoRegionCode,
    dispensaryId,
    hasEnterpriseParams,
    hasDispensaryParam,
  } = useIrlEventParams();
  const user = useUser();

  const called = hasDispensaryParam ? dispensaryMutationCalled : enterpriseMutationCalled;
  const loading = hasDispensaryParam ? dispensaryMutationLoading : enterpriseMutationLoading;
  const error = hasDispensaryParam ? dispensaryMutationError : enterpriseMutationError;

  const successData = hasDispensaryParam
    ? dispensaryMutationData?.trackIrlEventByDispensary?.success
    : enterpriseMutationData?.trackIrlEvent?.success;

  const success = successData === true;
  const completed = called && !loading;
  const failed = completed && (error !== undefined || successData === false);

  const trackEvent = useCallback(
    async (event: IrlEventTrackingEvent): Promise<void> => {
      const session = Session.getInstance();
      const userCookie = session.data.id;

      if (!irlEventId || (!hasEnterpriseParams && !hasDispensaryParam) || !userCookie) {
        throw new Error('Missing required params for IRL event tracking');
      }

      const ecommUserId = user.id ?? null;
      const requiresAuth = event !== IrlEventTrackingEvent.open;

      if (requiresAuth && !ecommUserId) {
        throw new Error('Auth-required IRL event tracking attempted without a user id');
      }

      if (hasDispensaryParam) {
        await trackDispensary({
          variables: { input: { ecommUserId, event, irlEventId, userCookie, dispensaryId } },
        });
      } else {
        await trackEnterprise({
          variables: { input: { ecommUserId, event, irlEventId, userCookie, enterpriseId, isoRegionCode } },
        });
      }
    },
    [
      user.id,
      trackEnterprise,
      trackDispensary,
      irlEventId,
      enterpriseId,
      isoRegionCode,
      dispensaryId,
      hasEnterpriseParams,
      hasDispensaryParam,
    ]
  );

  return {
    trackEvent,
    called,
    loading,
    error,
    success,
    completed,
    failed,
  };
}
